<template>
  <div>
      <div v-for="(hit, index) in hits" :key="index">
        <div v-if="hit.section === 'team'">
          <ContactPersonItem
            class="w-1/2"
            :modifiers="['white', 'multiple', 'search']"
            :team-firstname="hit.teamFirstname"
            :team-lastname="hit.teamLastname"
            :team-job-description="hit.teamJobDescription"
            :team-telephone="hit.teamTelephone"
            :team-image-src="hit.teamImageSrc"
            :team-email="hit.teamEmail"
            :team-address="hit.teamAddress"
            :team-honorary-church-title="hit.teamHonoraryChurchTitle"
            :team-academic-degree="hit.teamAcademicDegree"
            :team-ordenskuerzel="hit.teamOrdenskuerzel"
            :team-role="hit.teamRole"
          />
        </div>
        <div v-if="hit.section === 'departments'">
          <ContactSearchItem
            :title="hit.title"
            :subtitle="hit.subtitle"
            :email="hit.teamEmail"
            :phone="hit.teamTelephone"
            :website="hit.website"
            :image="hit.image"
          />
        </div>
        <div v-if="hit.section === 'houses'">
          <ContactSearchItem
            :title="hit.title"
            :subtitle="hit.subTitle"
            :email="hit.teamEmail"
            :phone="hit.teamTelephone"
            :website="hit.website"
            :image="hit.image"
          />
        </div>
        <div v-if="hit.section === 'departments'">
          <ContactPersonItem
            class="w-1/2"
            :modifiers="['white', 'multiple', 'search']"
            :team-firstname="hit.teamFirstname"
            :team-lastname="hit.teamLastname"
            :team-job-description="hit.teamJobDescription"
            :team-telephone="hit.teamTelephone"
            :team-image-src="hit.teamImageSrc"
            :team-email="hit.teamEmail"
            :team-address="hit.teamAddress"
            :team-honorary-church-title="hit.teamHonoraryChurchTitle"
            :team-academic-degree="hit.teamAcademicDegree"
            :team-ordenskuerzel="hit.teamOrdenskuerzel"
            :team-role="hit.teamRole"
          />
        </div>
        <div v-if="hit.entryType === 'articles'">
          <SearchResult
            :modifiers="[]"
            :headline="hit._highlightResult.title.value"
            :category="hit.category"
            :date="hit.publishingDate ? hit.publishingDate.date : null"
            :teaser-text="getEntryTeaserText(hit)"
            :image-src="hit.entryImageSrc"
            :link="hit.link"
          />
        </div>
        <div v-else-if="hit.entryType === 'pages'">
          <SearchResult
            :modifiers="[]"
            :headline="hit._highlightResult.title.value"
            :category="hit.category"
            :teaser-text="getEntryTeaserText(hit)"
            :link="hit.link"
            :image-src="hit.entryImageSrc"
          />
        </div>
        <div v-else-if="hit.entryType === 'parishesAssociations'">
          <SearchResult
            :modifiers="[]"
            :headline="`Pfarrverband ${hit._highlightResult.title.value}`"
            :teaser-text="getEntryTeaserTextForParishAssociation(hit)"
            :link="hit.link"
            :image-src="hit.image"
            :target="'_blank'"
          />
        </div>
        <div v-else-if="hit.entryType === 'churches'">
          <SearchResult
            :modifiers="[]"
            :headline="hit._highlightResult.title.value"
            :teaser-text="hit.address"
            :link="hit.link"
            :image-src="hit.image"
          />
        </div>
        <div v-else-if="hit.entryType === 'events'">
          <TeaserDateSitebarItem
            :modifiers="['grey', 'search']"
            :headline="hit._highlightResult.title.value"
            :label="hit.label"
            :location-town="hit.locationTown"
            :location-title="hit.locationTitle"
            :start-date="hit.startDate ? hit.startDate.date : null"
            :end-date="hit.endDate ? hit.endDate.date : null"
            :start-time="hit.startTime ? hit.startTime.date : null"
            :end-time="hit.endTime ? hit.endTime.date : null"
            :link="hit.link"
          />
        </div>
        <div v-else-if="hit.entryType == 'products'" class="m-searchResult">
          <ProductCard
            :title="hit._highlightResult.title.value"
            :price="hit.price"
            :subtitle="hit.productSubline"
            :image="hit.image"
            :link="hit.link"
            :altTitle="hit.title"
          />
        </div>
      </div>
    <slot />
  </div>
</template>

<script setup lang="ts">
import TeaserDateSitebarItem from '../teaserDateSitebarItem/_component.vue';
import SearchResult from '../searchResult/_component.vue';
import ProductCard from '../productCard/_component.vue';
import ContactSearchItem from '../contactSearch/contactSearchItem.vue';
import ContactPersonItem from '../contactPersonItem/_component.vue';
import { computed } from 'vue';

interface IResultListProps {
  hits: any[];
}

const props = defineProps<IResultListProps>();

function isFullyMatched(highlightResult) {
  return highlightResult.matchLevel === 'full';
}

function getEntryTeaserText(hit) {
  let text = null;
  const { entryTeaserText, content } = hit._snippetResult ?? {};

  // check if entry text is a match
  if (entryTeaserText && isFullyMatched(entryTeaserText)) {
    text = entryTeaserText.value;
  } else if (content) {
    // else search first content element that matches
    const contentLength = content.length;
    let index = 0;
    let result = false;
    while (index < contentLength && !result) {
      const contentEntry = Object.values(content[index])[0];
      if (isFullyMatched(contentEntry)) {
        text = contentEntry.value;
        result = true;
      }
      index += 1;
    }
    if (!result) {
      // if no content element matches, use entry text anyway
      text = entryTeaserText ? entryTeaserText.value : null;
    }
  }
  return text;
}

function getEntryTeaserTextForParishAssociation(hit) {
  let text = null;
  const { entryIntroText } = hit._snippetResult;
  const { locationDescription } = hit._snippetResult;
  const { churchesAndParishes } = hit._snippetResult;

  // Check if entry text is a match
  if (entryIntroText && isFullyMatched(entryIntroText)) {
    text = entryIntroText.value;

    // Check for match in parishes and churches
  } else if (churchesAndParishes && isFullyMatched(churchesAndParishes)) {
    text = churchesAndParishes.value;

    // Else check for match in location description
  } else if (locationDescription && isFullyMatched(locationDescription)) {
    text = locationDescription.value;
  } else {
    text = entryIntroText ? entryIntroText.value : null;
  }

  return text;
}
</script>
